import type { Category, Item, ItemId, Person, PersonId } from '../types.js';

/**
 * The operation language the model emits.
 *
 * The model never computes and never returns prose — it returns operations
 * that reference IDs it was handed. That constraint is what makes hallucination
 * *detectable*: an invented person or item is a failed ID lookup, caught before
 * a single cent moves. Free-form output has no equivalent safety net.
 */

export type PersonSet =
  /** "Alex and me" */
  | { kind: 'only'; ids: PersonId[] }
  /** "all of us minus Kelly" — the phrasing from the original idea. */
  | { kind: 'allExcept'; ids: PersonId[] };

export type ItemSelector =
  | { kind: 'ids'; ids: ItemId[] }
  /** "the alcohol", "the meat" */
  | { kind: 'category'; category: Category }
  /** "everything else" — resolved against what is still unclaimed. */
  | { kind: 'rest' };

export type SplitOp =
  | {
      kind: 'assign';
      items: ItemSelector;
      to: PersonSet;
      /** Uneven shares, parallel to `to.ids`. Only valid with `only`. */
      weights?: number[];
    }
  | { kind: 'unassign'; items: ItemSelector; from: PersonSet };

export interface SplitContext {
  items: Item[];
  people: Person[];
  /**
   * Who is talking. Needed to resolve "me" and "I" — the phrasing in the
   * original idea is "Alex and me are splitting the alcohol", and without this
   * the model has to guess which roster entry the speaker is.
   */
  speakerId?: PersonId;
}

export interface OpError {
  opIndex: number;
  message: string;
}

const personSetOf = (op: SplitOp): PersonSet => (op.kind === 'assign' ? op.to : op.from);

/**
 * Reject anything referencing an entity that does not exist.
 *
 * This runs before resolution, on every model response, without exception.
 */
export function validateOps(ops: SplitOp[], context: SplitContext): OpError[] {
  const knownItems = new Set(context.items.map((item) => item.id));
  const knownPeople = new Set(context.people.map((person) => person.id));
  const errors: OpError[] = [];

  ops.forEach((op, opIndex) => {
    const fail = (message: string): void => {
      errors.push({ opIndex, message });
    };

    if (op.items.kind === 'ids') {
      if (op.items.ids.length === 0) fail('item selector lists no items');
      for (const id of op.items.ids) {
        if (!knownItems.has(id)) fail(`unknown item id "${id}"`);
      }
    }

    const people = personSetOf(op);
    for (const id of people.ids) {
      if (!knownPeople.has(id)) fail(`unknown person id "${id}"`);
    }
    if (people.kind === 'only' && people.ids.length === 0) {
      fail('person set lists nobody');
    }
    if (new Set(people.ids).size !== people.ids.length) {
      fail('person set repeats an id');
    }

    if (op.kind === 'assign' && op.weights !== undefined) {
      if (people.kind !== 'only') {
        fail('weights require an explicit person list');
      } else if (op.weights.length !== people.ids.length) {
        fail('weights length does not match the person list');
      }
      for (const weight of op.weights) {
        if (!Number.isInteger(weight) || weight < 1) {
          fail(`weight must be a positive integer, got ${weight}`);
        }
      }
    }
  });

  return errors;
}

/** Expand a person set against the roster, preserving roster order. */
export function resolvePersonSet(set: PersonSet, people: Person[]): PersonId[] {
  if (set.kind === 'only') {
    const wanted = new Set(set.ids);
    return people.filter((person) => wanted.has(person.id)).map((person) => person.id);
  }
  const excluded = new Set(set.ids);
  return people.filter((person) => !excluded.has(person.id)).map((person) => person.id);
}
